import { useState, useEffect } from "react";
import { Box, Text } from "@/ui";
import { MenuItem, Select, FormControl, useTheme } from "@mui/material";
import { useSystemContext } from "@/provider/common/SystemProvider";
import {
  getSelectedPraticienFiness,
  setSelectedPraticienFiness,
} from "@/utils/localStorage";
import PersonIcon from "@mui/icons-material/Person";

type Praticien = {
  id?: string | number;
  nom?: string;
  prenom?: string;
  finess?: string;
  name?: string;
  firstname?: string;
};

type Props = {
  onPraticienChange?: (praticien: Praticien | null) => void;
  variant?: "compact" | "full";
  showLabel?: boolean;
};

export function PraticienSelector({ onPraticienChange, variant = "full", showLabel = true }: Props) {
  const theme = useTheme();
  const { getUser } = useSystemContext();
  const user = getUser();
  const praticiens: Praticien[] = user?.praticiens || [];

  const [selectedFiness, setSelectedFiness] = useState<string>(() => {
    const storedFiness = getSelectedPraticienFiness();
    if (storedFiness && praticiens.some((p) => p.finess === storedFiness)) {
      return storedFiness;
    }
    return praticiens[0]?.finess || "";
  });

  useEffect(() => {
    if (praticiens.length > 0) {
      const storedFiness = getSelectedPraticienFiness();
      const validFiness = praticiens.some((p) => p.finess === storedFiness);

      if (!storedFiness || !validFiness) {
        const defaultFiness = praticiens[0]?.finess || "";
        setSelectedFiness(defaultFiness);
        if (defaultFiness) {
          setSelectedPraticienFiness(defaultFiness);
        }
      }
    } else {
        setSelectedPraticienFiness(praticiens[0]?.finess || "");
    }
  }, [praticiens]);

  const handleChange = (event: any) => {
    const finess = event.target.value as string;
    setSelectedFiness(finess);
    setSelectedPraticienFiness(finess);

    const selectedPraticien = praticiens.find((p) => p.finess === finess) || null;
    onPraticienChange?.(selectedPraticien);

    // Recharger la page pour que les données soient récupérées avec le nouveau FINESS
    window.location.reload();
  };

  const getPraticienLabel = (praticien: Praticien) => {
    const nom = praticien.nom || praticien.name || "";
    const prenom = praticien.prenom || praticien.firstname || "";
    return `${prenom} ${nom}`.trim() || praticien.finess || "Praticien";
  };

  if (!praticiens || praticiens.length === 0) {
    return null;
  }

  if (praticiens.length === 1) {
    return null;
  }

  if (variant === "compact") {
    return (
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <Select
          value={selectedFiness}
          onChange={handleChange}
          displayEmpty
          sx={{
            fontSize: "12px",
            height: "32px",
            backgroundColor: "white",
            borderRadius: "8px",
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              gap: 1,
            },
          }}
          aria-label="Sélectionner un praticien"
          tabIndex={0}
        >
          {praticiens.map((praticien) => (
            <MenuItem
              key={praticien.finess || praticien.id}
              value={praticien.finess}
              sx={{ fontSize: "12px" }}
            >
              {praticien.finess && (
                <Text fontSize={12} color={theme.palette.grey[500]} ml={1}>
                  {praticien.finess}
                </Text>
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return (
    <Box flexDirection="column" width="100%" mb={2}>
      {showLabel && (
      <Box alignItems="center" mb={1}>
        <PersonIcon
          sx={{
            color: theme.palette.primary.main,
            fontSize: "20px",
            marginRight: "8px",
          }}
        />
        <Text fontSize={14} color={theme.palette.grey[600]}>
          Praticien sélectionné
        </Text>
      </Box>
      )}
      <FormControl fullWidth size="small">
        <Select
          value={selectedFiness}
          onChange={handleChange}
          displayEmpty
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.grey[300],
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: theme.palette.primary.main,
            },
          }}
          aria-label="Sélectionner un praticien"
          tabIndex={0}
        >
          {praticiens.map((praticien) => (
            <MenuItem
              key={praticien.finess || praticien.id}
              value={praticien.finess}
            >
              <Box alignItems="center">
                <Text fontSize={14}>{getPraticienLabel(praticien)}</Text>
                {praticien.finess && (
                  <Text
                    fontSize={12}
                    color={theme.palette.grey[500]}
                    ml={1}
                  >
                    ({praticien.finess})
                  </Text>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}



